import type {
    TileableModel,
    TileViewModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { Tile } from '@rocco/ui/tile-shared/components/Tile';
import { useTileAdminActions } from '@modules-rocco/admin/hooks/use-tile-admin-actions';
import { isProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { isProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { isProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { isArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import { useLogImpression } from '@archipro-website/tracker';
import { useRef } from 'react';
import { cn } from '@rocco/utils/cn';
import {
    TileEnquiryOverlay,
    useBulkEnquirySelectActive,
    useTileEnquiryClickCapture,
} from '~/modules/enquiry/bulk/BulkEnquireTileControl';
import {
    TileOverlayWithFollowSelector,
    TileMobileActionsSlot,
} from '~/modules/moodboards/components';
import { buildTileActionInfo } from '~/modules/moodboards/utils/tile-action-info';
import { buildTileVisitWebsite } from '~/modules/moodboards/utils/tile-visit-website';
import type { PinItemType } from '~/modules/moodboards/type';
import { useAppData } from '@modules/root';
import { useMatchesHandleData } from '~/modules/root/hook';

/**
 * Props interface for the CategoryPageTileWithAdminActions component
 * @interface CategoryPageTileWithAdminActionsProps
 * @property {TileViewModel<TileableModel>} tile - The tile view model to render with admin actions
 */
interface CategoryPageTileWithAdminActionsProps {
    tile: TileViewModel<TileableModel>;
    /** Show the item-type badge on the tile media (mixed-type carousels). */
    showTypeBadge?: boolean;
    featuredProjectIds?: number[];
    unfeaturedIds?: Set<number>;
    newlyFeaturedIds?: Set<number>;
    removedFromLibraryIds?: Set<number>;
    onToggleFeature?: (projectId: number, feature: boolean) => void;
    onRemoveFromLibrary?: (
        itemType: 'project' | 'product',
        itemId: number,
        itemName: string,
    ) => void;
}

/**
 * Component that renders a tile with admin actions if applicable
 * Wraps the base Tile component and adds admin functionality for authorized users
 * Handles different item types (professionals, products, projects) with appropriate links
 * Uses the new TileFollowButtonWithPopover for native popover positioning.
 * @param {CategoryPageTileWithAdminActionsProps} props - Component props
 * @returns {React.JSX.Element} The rendered tile with admin actions
 */
export const CategoryPageTileWithAdminActions = (
    props: CategoryPageTileWithAdminActionsProps,
) => {
    const {
        tile,
        showTypeBadge,
        featuredProjectIds = [],
        unfeaturedIds,
        newlyFeaturedIds,
        removedFromLibraryIds,
        onToggleFeature,
        onRemoveFromLibrary,
    } = props;
    const { user } = useAppData();
    const isBMPreview = useMatchesHandleData('isBMPreview', false);
    // No save-to-design-board UI inside the BM preview or share previews.
    const hideDesignBoardUI = isBMPreview || (tile.disableDesignBoard ?? false);
    const isStaffAdmin =
        user?.__typename === 'Me' && user.IsStaffAdmin === true;
    const isHighAdmin =
        user?.__typename === 'Me' &&
        'IsHighAdmin' in user &&
        (user as Record<string, unknown>).IsHighAdmin === true;
    const canFeature = isStaffAdmin || isHighAdmin;
    const tileRef = useRef<HTMLDivElement>(null);
    const model = tile.model;

    // Check if user can edit this item - use canEdit from model if available,
    // otherwise fall back to adminStatusInfo (for backward compatibility)
    let canEdit = 'canEdit' in model ? model.canEdit : !!tile.adminStatusInfo;
    if (isProjectTileViewModel(model) && !isStaffAdmin) {
        canEdit = false;
    }

    // Check if this tile is currently animating (fading out or fading in)
    const isFadingOut =
        (unfeaturedIds?.has(model.id) ?? false) ||
        (removedFromLibraryIds?.has(model.id) ?? false);
    const isFadingIn = newlyFeaturedIds?.has(model.id) ?? false;

    // Feature toggle: projects only, admins with canFeature permission
    const isThisTileFeatured =
        isProjectTileViewModel(model) && featuredProjectIds.includes(model.id);
    const showFeatureOption =
        canFeature &&
        isProjectTileViewModel(model) &&
        onToggleFeature &&
        !isFadingOut;

    const featureToggleAction = showFeatureOption
        ? () => onToggleFeature(model.id, !isThisTileFeatured)
        : undefined;

    const itemType = getItemTypeLink(model);
    const removeFromLibraryAction =
        canFeature &&
        onRemoveFromLibrary &&
        (isProjectTileViewModel(model) || isProductTileViewModel(model)) &&
        !isFadingOut
            ? () =>
                  onRemoveFromLibrary(
                      isProjectTileViewModel(model) ? 'project' : 'product',
                      model.id,
                      'title' in model ? (model.title ?? '') : '',
                  )
            : undefined;

    const adminActions = useTileAdminActions({
        item: {
            id: model.id.toString(),
            link: 'link' in model ? (model.link ?? '') : '',
            professionalLink: getProfessionalLink(model),
            pinned: tile.adminStatusInfo?.pinned,
            featuredBrandPinned: tile.adminStatusInfo?.featuredBrandPinned,
            productsAllowed: tile.adminStatusInfo?.productsAllowed,
        },
        itemType,
        onToggleFeature: featureToggleAction,
        onRemoveFromLibrary: removeFromLibraryAction,
    });

    // Enrich adminStatusInfo with featured state for the context menu label
    const enrichedTile = showFeatureOption
        ? {
              ...tile,
              adminStatusInfo: {
                  ...(tile.adminStatusInfo ?? {
                      created: { createdBy: '', createdAt: '' },
                  }),
                  ...(isThisTileFeatured ? { featured: true } : {}),
              },
          }
        : tile;

    useLogImpression({
        data: {
            type: 'LibraryItem_Directory',
            itemID: model.id,
            professionalID: getProfessionalId(model),
        },
        ref: tileRef,
    });

    const pinType = getPinType(model);

    // Bulk-enquiry mode: the whole tile toggles the selection instead of
    // navigating (capture phase, so it beats the link inside <Tile>).
    const enquiryClickCapture = useTileEnquiryClickCapture(model);
    const bulkSelectActive = useBulkEnquirySelectActive(model);

    // Enquiry-first tile panel (tile_enquiry_panel flag): product /
    // professional / project tiles get an Enquire button (a project enquires
    // its professional); article tiles fall back to More-only. Product must be
    // checked before project — the duck-typed guards overlap (product models
    // carry `hasAwards`/`badge` too).
    const enquiryItemType = isProductTileViewModel(model)
        ? ('product' as const)
        : isProfessionalTileViewModel(model)
          ? ('professional' as const)
          : isProjectTileViewModel(model)
            ? ('project' as const)
            : null;
    const professionalLink = getProfessionalLink(model);
    const tileEnquiry =
        enquiryItemType && professionalLink
            ? {
                  itemType: enquiryItemType,
                  siteTreeID: model.id,
                  professionalId: getProfessionalId(model),
                  professionalName: isProfessionalTileViewModel(model)
                      ? model.title
                      : isProductTileViewModel(model) ||
                          isProjectTileViewModel(model)
                        ? (model.professional?.name ?? '')
                        : '',
                  professionalLink,
                  itemTitle: 'title' in model ? model.title : undefined,
                  // Professional tiles already show that professional's logo in
                  // the modal header, so only product / project tiles add a
                  // thumbnail of the item being enquired about.
                  itemImage:
                      isProductTileViewModel(model) ||
                      isProjectTileViewModel(model)
                          ? model.images?.[0]?.src
                          : undefined,
              }
            : null;
    const tileShare =
        'link' in model && model.link
            ? {
                  url: model.link,
                  title: 'title' in model ? (model.title ?? '') : '',
              }
            : null;
    const tileInfo = buildTileActionInfo(model);
    const tileVisitWebsite = buildTileVisitWebsite(model);

    return (
        <div
            ref={tileRef}
            onClickCapture={enquiryClickCapture}
            className={cn(
                'relative transition-all duration-500 ease-in-out',
                (isFadingOut || isFadingIn) &&
                    'pointer-events-none scale-95 opacity-0',
            )}
        >
            <Tile
                tile={{
                    ...enrichedTile,
                    hideFollowButton:
                        hideDesignBoardUI || tile.hideFollowButton,
                    overlay: hideDesignBoardUI ? undefined : (
                        <TileEnquiryOverlay
                            model={model}
                            follow={
                                <TileOverlayWithFollowSelector
                                    pinId={model.id}
                                    pinType={pinType}
                                    enableActionPanel
                                    enquiry={tileEnquiry}
                                    visitWebsite={tileVisitWebsite}
                                    share={tileShare}
                                    tileInfo={tileInfo}
                                    // Square-cornered tiles (5.0 browse grid)
                                    // need a square hover overlay too.
                                    rounded={enrichedTile.rounded !== false}
                                />
                            }
                        />
                    ),
                }}
                adminActions={
                    canEdit
                        ? adminActions
                        : adminActions.onToggleFeature ||
                            adminActions.onRemoveFromLibrary ||
                            adminActions.onTogglePin
                          ? {
                                onToggleFeature: adminActions.onToggleFeature,
                                onRemoveFromLibrary:
                                    adminActions.onRemoveFromLibrary,
                                onTogglePin: adminActions.onTogglePin,
                            }
                          : undefined
                }
                showTypeBadge={showTypeBadge}
            />
            {/* Mobile actions live outside the tile link so a tap on the `•••`
                or a sheet-backdrop dismiss can never navigate the tile. Stands
                down in bulk-enquiry mode, where the tile is a select target. */}
            {!hideDesignBoardUI && !bulkSelectActive ? (
                <TileMobileActionsSlot
                    pinId={model.id}
                    pinType={pinType}
                    enableActionPanel
                    enquiry={tileEnquiry}
                    visitWebsite={tileVisitWebsite}
                    share={tileShare}
                    tileInfo={tileInfo}
                />
            ) : null}
        </div>
    );
};

/**
 * Helper function to get the professional link from a tile model
 * Handles different model types that may contain professional links
 * @param {TileableModel} model - The tile model to extract professional link from
 * @returns {string} The professional link or empty string if not found
 */
const getProfessionalLink = (model: TileableModel): string => {
    if (isProfessionalTileViewModel(model)) {
        return model.link;
    }

    if (isProductTileViewModel(model)) {
        return model.professional?.link ?? '';
    }

    if (isProjectTileViewModel(model)) {
        return model.professional?.link ?? '';
    }

    return '';
};

const getProfessionalId = (model: TileableModel): number => {
    if (isProfessionalTileViewModel(model)) {
        return model.id;
    }

    if (isProductTileViewModel(model)) {
        return model.professional.id;
    }

    if (isProjectTileViewModel(model)) {
        return model.professional.id;
    }

    return 0;
};

/**
 * Helper function to determine the item type for admin actions
 * Maps different model types to their corresponding admin section
 * @param {TileableModel} model - The tile model to determine type for
 * @returns {string} The item type identifier for admin actions
 */
const getItemTypeLink = (model: TileableModel) => {
    if (isProfessionalTileViewModel(model)) {
        return 'professionals';
    }

    if (isProductTileViewModel(model)) {
        return 'products';
    }

    return 'projects';
};

/**
 * Helper function to get the pin type for the follow button.
 * @param {TileableModel} model - The tile model
 * @returns {PinItemType} The pin type for saving to boards
 */
export const getPinType = (model: TileableModel): PinItemType => {
    if (isProfessionalTileViewModel(model)) {
        return 'ProfessionalPin';
    }

    if (isProductTileViewModel(model)) {
        return 'ProductPin';
    }

    if (isProjectTileViewModel(model)) {
        return 'ProjectPin';
    }

    if (isArticleTileViewModel(model)) {
        return 'ArticlePin';
    }

    return 'ProductPin'; // default fallback
};
